import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Icon } from '../Icon/Icon.es.js';
import { Animation } from '../../../theme/Animation.es.js';
import { Colors, ColorsCSS } from '../../../theme/Colors.es.js';
import { Shadows } from '../../../theme/Shadows.es.js';
import { TypographyPDL } from '../../../theme/Typography.es.js';
import '../../../theme/ZinniaStylesReset.es.js';

const baseTypeConfigs = {
  info: {
    backgroundColor: "blue600",
    borderColor: "",
    color: "neutral100",
    icon: "alertInfoFill",
    iconColor: "blue600",
    toastIconColor: `var(--zds-ui-primary, ${Colors.blue600})`,
    toastStripColor: `var(--zds-ui-primary, ${Colors.blue600})`
  },
  success: {
    backgroundColor: "success400",
    borderColor: "",
    color: "neutral700",
    icon: "formCheckCircle",
    iconColor: "success400",
    toastIconColor: `var(--zds-status-success, ${Colors.success400})`,
    toastStripColor: `var(--zds-status-success, ${Colors.success400})`
  },
  warning: {
    backgroundColor: "warning400",
    // TODO: change to warning200 after PDL is fully implemented, being overwritten below
    borderColor: "",
    color: "neutral700",
    icon: "alertCircleColor",
    iconColor: "neutral700",
    toastIconColor: `var(--zds-gray-warm-9, ${Colors.neutral700})`,
    toastStripColor: `var(--zds-status-warning-weaker, ${Colors.warning400})`
  },
  error: {
    backgroundColor: "error400",
    borderColor: "",
    color: "neutral100",
    icon: "formXCircle",
    iconColor: "error400",
    toastIconColor: `var(--zds-status-error, ${Colors.error400})`,
    toastStripColor: `var(--zds-status-error, ${Colors.error400})`
  }
};
const bannerTypeConfigs = {
  info: Object.assign(Object.assign({}, baseTypeConfigs.info), { borderColor: "var(--zds-ui-primary)" }),
  success: {
    backgroundColor: "success100",
    borderColor: "var(--zds-status-success)",
    color: "neutral700",
    icon: "formCheckCircle",
    iconColor: "success400",
    toastIconColor: `var(--zds-status-success, ${Colors.success400})`,
    toastStripColor: `var(--zds-status-success, ${Colors.success400})`
  },
  warning: {
    backgroundColor: "warning100",
    borderColor: "var(--zds-status-warning-weaker)",
    color: "neutral700",
    icon: "alertCircleColor",
    iconColor: "neutral700",
    toastIconColor: `var(--zds-status-warning-weaker, ${Colors.warning400})`,
    toastStripColor: `var(--zds-status-warning-weaker, ${Colors.warning400})`
  },
  error: {
    backgroundColor: "error100",
    borderColor: "var(--zds-status-error)",
    color: "neutral700",
    icon: "formXCircle",
    iconColor: "error400",
    toastIconColor: `var(--zds-status-error, ${Colors.error400})`,
    toastStripColor: `var(--zds-status-error, ${Colors.error400})`
  }
};
const typeConfigs = {
  box: baseTypeConfigs,
  banner: bannerTypeConfigs,
  "banner-inverse": baseTypeConfigs,
  compact: bannerTypeConfigs,
  default: bannerTypeConfigs,
  modal: baseTypeConfigs,
  mini: baseTypeConfigs,
  toast: baseTypeConfigs
};
const Styles = labelStyles("Alert", {
  root: ({ hasShadow, layout, miniBackgroundColor = "blue600", type }) => [
    (layout === "box" || layout === "toast") && [
      css`
        label: -box;
        position: relative;
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        align-items: start;
        gap: var(--zds-space-8, 10px);
        padding-top: var(--zds-space-8, 10px);
        padding-right: var(--zds-space-16, 10px);
        padding-bottom: var(--zds-space-8, 10px);
        padding-left: var(--zds-space-16, 20px);
        border-radius: var(--zds-radius-none, 5px);
        background-color: var(--zds-background-weaker, ${ColorsCSS.neutral100});
        ${TypographyPDL("--zds-body-1", "paragraph3")};
        color: var(--zds-gray-warm-9, ${ColorsCSS.neutral700});

        // Colored stripe that renders on the left of the box.
        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          border-top-left-radius: var(--zds-radius-none, 4px);
          border-bottom-left-radius: var(--zds-radius-none, 4px);
          width: 10px;
        }
      `,
      css`
        label: -${type};
        &::before {
          background-color: ${ColorsCSS[typeConfigs[layout][type].backgroundColor]};
        }
      `,
      layout === "toast" && css`
          &::before {
            background-color: ${typeConfigs[layout][type].toastStripColor};
          }
        `
    ],
    // TODO: Remove after PDL is fully implemented, overriding warning400 in baseTypeConfigs above
    type === "warning" && [
      css`
        label: -warning;
        &::before {
          background-color: var(
            --zds-status-warning-weaker,
            ${Colors.warning400}
          );
        }
      `
    ],
    (layout === "banner" || layout === "banner-inverse" || layout === "compact" || layout === "default") && [
      css`
        label: -banner;
        position: relative;
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        gap: 10px;
        align-items: start;
        ${TypographyPDL("--zds-body-1", "paragraph3")};
        /* TODO: Apply this padding to banner-inverse  */
        padding-top: var(--zds-space-8, 10px);
        padding-right: var(--zds-space-16, 44px);
        padding-bottom: var(--zds-space-8, 10px);
        padding-left: var(--zds-space-16, 44px);
        text-align: start;
      `,
      css`
        label: -${type};
        color: ${ColorsCSS[typeConfigs[layout][type].color]};
        background-color: ${ColorsCSS[typeConfigs[layout][type].backgroundColor]};
        /* TODO: use transparent border in banner-inverse to prevent 2px height difference between banner and banner-inverse */
        border: 1px solid ${typeConfigs[layout][type].borderColor};
        border-radius: var(--zds-radius-medium);
      `,
      layout === "compact" && css`
          ${TypographyPDL("--zds-minimal-print-1", "paragraph3")};
          gap: var(--zds-space-8, 10px);
          padding: var(--zds-space-8, 8px);
        `,
      (layout === "banner" || layout === "banner-inverse") && css`
          border: none;
          border-radius: var(--zds-radius-none, 0);
        `,
      // Overrides info banner colors, which don't map 1:1 between legacy and PDL
      type === "info" && css`
          color: var(--zds-text-default, ${Colors.neutral100});
          background-color: var(--zds-ui-primary-weakest, ${Colors.blue600});
        `,
      layout !== "banner-inverse" && [
        type === "info" && css`
            // Rare tag selection to ensure that links are accessible
            // and consumers don't need to worry about it.
            a[class] {
              color: var(--zds-text-link, inherit);
              text-decoration: underline;
              &:hover,
              &:focus {
                color: var(
                  --zds-text-link-hover,
                  rgba(${ColorsCSS.neutral100rgb}, 0.75)
                );
              }
              &:focus {
                outline: 1px solid
                  var(
                    --zds-text-link-hover,
                    rgba(${ColorsCSS.neutral100rgb}, 0.75)
                  );
              }
            }
          `
      ]
    ],
    layout === "banner-inverse" && [
      css`
        label: -banner;
        position: relative;
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        gap: 10px;
        align-items: start;
        ${TypographyPDL("--zds-body-1", "paragraph3")};
        text-align: start;
        border: none;
      `,
      css`
        label: -${type};
        color: var(--zds-text-default, ${ColorsCSS.neutral700});
        background-color: var(--zds-background-weaker, ${ColorsCSS.neutral100});
      `
    ],
    layout === "mini" && [
      css`
        label: -mini;
        position: relative;
        display: grid;
        align-items: center;
        ${TypographyPDL("--zds-body-2", "paragraph3Medium")};
        text-align: center;
        padding: 8px 44px;
      `,
      css`
        label: -${type};
        background-color: ${ColorsCSS[miniBackgroundColor]};
        color: ${miniBackgroundColor === "neutral100" ? ColorsCSS.neutral800 : ColorsCSS.neutral100};

        a[class] {
          color: inherit;
          text-decoration: underline;
          &:hover,
          &:focus {
            color: ${miniBackgroundColor === "neutral100" ? ColorsCSS.blueJeans : `rgba(${ColorsCSS.neutral100rgb}, 0.75)`};
          }
          &:focus {
            outline: 1px solid
              ${miniBackgroundColor === "neutral100" ? ColorsCSS.blueJeans : `rgba(${ColorsCSS.neutral100rgb}, 0.75)`};
          }
        }
      `
    ],
    layout === "modal" && [
      css`
        label: -modal;
        position: relative;
        max-width: 500px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: var(--zds-prime-white, ${ColorsCSS.neutral100});
        padding: 15px;
        border: 1px solid var(--zds-gray-warm-5, ${ColorsCSS.neutral300}); // TODO: Change border value to --zds-border-default for PDL
        text-align: center;
        border-radius: 10px;
      `
    ],
    hasShadow && css`
        label: -shadowed;
        box-shadow: var(--zds-shadow-surface, ${Shadows.elevation5});
      `
  ],
  typeIcon: ({ layout, type }) => [
    css`
      color: ${ColorsCSS[typeConfigs[layout][type].iconColor]};
    `,
    layout === "modal" && css`
        margin-bottom: 8px;
      `,
    (layout === "banner" || layout === "banner-inverse" || layout === "compact" || layout === "default") && [
      css`
        color: ${type === "info" ? "currentColor" : ColorsCSS[typeConfigs[layout][type].iconColor]};
        display: inline-block;
        vertical-align: middle;
        margin-top: 2px;
        margin-right: 5px;
        position: relative;
        top: -2px;
      `,
      // Overrides info banner icon color, which doesn't map 1:1 between legacy and PDL
      (layout === "banner" || layout === "compact" || layout === "default") && type === "info" && css`
          color: var(--zds-ui-primary-stronger);
        `
    ],
    layout === "toast" && css`
        color: ${typeConfigs[layout][type].toastIconColor};
      `
  ],
  titleInner: ({ layout, type }) => [
    css`
      display: block;
      margin: 0;
      ${TypographyPDL("--zds-body-3", "paragraph3Bold")};
      color: var(--zds-text-default, ${ColorsCSS.neutral800});
    `,
    (layout === "banner" || layout === "compact" || layout === "default") && [
      css`
        color: inherit;
      `,
      // Overrides info banner title color, which doesn't map 1:1 between legacy and PDL
      type === "info" && css`
          color: var(--zds-text-stronger);
        `
    ],
    layout === "compact" && css`
        ${TypographyPDL("--zds-small-print-3", "paragraph3Bold")};
      `,
    layout === "mini" && css`
        color: inherit;
      `,
    layout === "modal" && css`
        ${TypographyPDL("--zds-paragraph-header-3", "paragraph3Bold")};
      `
  ],
  closeButton: ({ miniBackgroundColor, layout, type }) => [
    // TODO: Change outline color value to `--zds-stroke-stronger` after PDL is fully implemented
    layout !== "banner" && layout !== "mini" && layout !== "default" && css`
        color: ${ColorsCSS.neutral600};
        transition: ${Animation.transitionValue};

        &:hover,
        &:focus {
          outline: solid 1px ${ColorsCSS.neutral600};
        }
      `,
    (layout === "banner" || layout === "compact" || layout === "default") && [
      css`
        color: inherit;

        &:hover,
        &:focus {
          outline: solid 1px
            ${type === "info" ? `var(--zds-stroke-stronger, ${ColorsCSS.neutral100})` : ColorsCSS.neutral600};
        }
      `,
      // Overrides info banner title color, which doesn't map 1:1 between legacy and PDL
      type === "info" && css`
          color: var(--zds-stroke-stronger);
        `
    ],
    layout === "mini" && css`
        color: ${miniBackgroundColor === "neutral100" ? ColorsCSS.neutral600 : "inherit"};

        &:hover,
        &:focus {
          outline: 1px solid
            ${miniBackgroundColor === "neutral100" ? ColorsCSS.neutral600 : ColorsCSS.neutral100};
        }
      `,
    (layout === "banner" || layout === "banner-inverse" || layout === "compact") && css`
        position: absolute;
        right: 15px;
        top: 22px;
        transform: translateY(-50%);
      `,
    layout === "mini" && css`
        position: absolute;
        right: 15px;
        top: 20px;
        transform: translateY(-50%);
      `,
    layout === "modal" && css`
        position: absolute;
        top: 15px;
        right: 15px;
      `
  ]
});
const Alert = ({ ariaLive = void 0, children = void 0, hasShadow = true, layout = "box", onDismiss = void 0, role = void 0, title = void 0, type = "info", miniBackgroundColor = void 0 }) => {
  const config = typeConfigs[layout][type];
  const isMini = layout === "mini";
  const icon = jsx(
    "div",
    { css: Styles.typeIcon({ layout, type }) },
    jsx(Icon, { ariaLabel: type, isBlock: true, name: config.icon, size: layout === "compact" ? 20 : 24 })
  );
  const closeButton = onDismiss && jsx(
    "button",
    { type: "button", "aria-label": "Dismiss", css: Styles.closeButton({ miniBackgroundColor, layout, type }), onClick: onDismiss, "data-zds": true },
    jsx(Icon, { isBlock: true, name: "formX", size: 24 })
  );
  return jsx(
    "article",
    { "aria-live": ariaLive, css: Styles.root({ hasShadow, layout, miniBackgroundColor, type }), role, "data-zds": true },
    !isMini && icon,
    jsx(
      "div",
      { css: Styles.content, "data-zds": true },
      title && !isMini && jsx("h2", { css: Styles.titleInner({ layout, type }), "data-zds": true }, title),
      children
    ),
    closeButton
  );
};

export { Alert };
//# sourceMappingURL=Alert.es.js.map
